import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { TicketStatus, Priority } from '../../types/ticket';
import type { Ticket, Tech } from '../../types/ticket';
import {
  Box,
  Typography,
  AppSelect,
  AppButton,
  AppChip,
  Stack,
  Divider,
} from '../../components/ui';
import { useTicketRepository, useTechRepository } from '../../data/hooks';
import './AnalyticsExplorer.css';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type MetricType =
  | 'tickets_created'
  | 'tickets_resolved'
  | 'avg_resolution_time'
  | 'sla_breach_count'
  | 'backlog_size';

type TimeRangeType = '7d' | '14d' | '30d' | '90d';
type GroupByType = 'day' | 'week' | 'hour_of_day' | 'day_of_week';
type SplitByType = 'none' | 'tech' | 'priority' | 'location' | 'category';
type ChartType = 'line' | 'bar' | 'area';

interface FilterState {
  metric: MetricType;
  timeRange: TimeRangeType;
  groupBy: GroupByType;
  splitBy: SplitByType;
  chartType: ChartType;
  priorities: Priority[];
}

interface DataPoint {
  label: string;
  [key: string]: string | number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const METRIC_OPTIONS: { value: MetricType; label: string; unit: string }[] = [
  { value: 'tickets_created', label: 'Tickets Created', unit: 'tickets' },
  { value: 'tickets_resolved', label: 'Tickets Resolved', unit: 'tickets' },
  { value: 'avg_resolution_time', label: 'Avg Resolution Time', unit: 'minutes' },
  { value: 'sla_breach_count', label: 'SLA Breaches', unit: 'tickets' },
  { value: 'backlog_size', label: 'Backlog Size', unit: 'tickets' },
];

const TIME_RANGE_OPTIONS: { value: TimeRangeType; label: string; days: number }[] = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '14d', label: 'Last 14 days', days: 14 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
];

const GROUP_BY_OPTIONS: { value: GroupByType; label: string }[] = [
  { value: 'day', label: 'By Day' },
  { value: 'week', label: 'By Week' },
  { value: 'hour_of_day', label: 'By Hour of Day' },
  { value: 'day_of_week', label: 'By Day of Week' },
];

const SPLIT_BY_OPTIONS: { value: SplitByType; label: string }[] = [
  { value: 'none', label: 'No Split' },
  { value: 'tech', label: 'By Technician' },
  { value: 'priority', label: 'By Priority' },
  { value: 'location', label: 'By Location' },
  { value: 'category', label: 'By Category' },
];

const CHART_TYPE_OPTIONS: { value: ChartType; label: string; icon: React.ReactNode }[] = [
  {
    value: 'line',
    label: 'Line',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M2 12L6 6L10 9L14 3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'bar',
    label: 'Bar',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <rect x="2" y="8" width="3" height="6" rx="0.5" />
        <rect x="6.5" y="4" width="3" height="10" rx="0.5" />
        <rect x="11" y="6" width="3" height="8" rx="0.5" />
      </svg>
    ),
  },
  {
    value: 'area',
    label: 'Area',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" opacity="0.6">
        <path d="M2 12L6 6L10 9L14 3V12H2Z" />
      </svg>
    ),
  },
];

// Color palettes
const SPLIT_COLORS = {
  tech: ['#1976d2', '#107c10', '#7b1fa2', '#c2185b', '#f57c00', '#00838f', '#5c6bc0', '#8d6e63'],
  priority: { P1: '#d13438', P2: '#ca5010', P3: '#8a8886', P4: '#107c10' },
  location: ['#0078d4', '#00bcf2', '#8764b8', '#e3008c', '#4f6bed'],
  category: ['#1976d2', '#107c10', '#ca5010', '#7b1fa2', '#00838f', '#c2185b'],
};

const PRIMARY_COLOR = '#1976d2';

// ============================================================================
// DATA GENERATION (Simulated - would come from API in production)
// ============================================================================

const generateTimeSeriesData = (
  filters: FilterState,
  tickets: Ticket[],
  techs: Tech[]
): DataPoint[] => {
  const { timeRange, groupBy, splitBy, metric } = filters;
  const days = TIME_RANGE_OPTIONS.find((t) => t.value === timeRange)?.days || 7;

  // Generate labels based on groupBy
  const labels: string[] = [];
  const now = new Date();

  if (groupBy === 'day') {
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
  } else if (groupBy === 'week') {
    const weeks = Math.ceil(days / 7);
    for (let i = weeks - 1; i >= 0; i--) {
      labels.push(`Week ${weeks - i}`);
    }
  } else if (groupBy === 'hour_of_day') {
    for (let i = 0; i < 24; i++) {
      labels.push(`${i.toString().padStart(2, '0')}:00`);
    }
  } else if (groupBy === 'day_of_week') {
    labels.push('Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun');
  }

  // Get split keys
  let splitKeys: string[] = ['value'];
  if (splitBy === 'tech') {
    splitKeys = techs.slice(0, 5).map((t) => t.name.split(' ')[0]);
  } else if (splitBy === 'priority') {
    splitKeys = ['P1', 'P2', 'P3', 'P4'];
  } else if (splitBy === 'location') {
    splitKeys = [...new Set(techs.map((t) => t.location))].slice(0, 5);
  } else if (splitBy === 'category') {
    const allTags = tickets.flatMap((t) => t.tags ?? []);
    splitKeys = [...new Set(allTags)].slice(0, 5);
  }

  // Generate data points with realistic patterns
  return labels.map((label, index) => {
    const dataPoint: DataPoint = { label };

    splitKeys.forEach((key, keyIndex) => {
      // Create realistic-looking data with patterns
      const baseValue = getBaseValue(metric, index, labels.length);
      const variance = Math.random() * 0.3 - 0.15; // ±15% variance
      const splitMultiplier = splitBy === 'none' ? 1 : 0.3 + (keyIndex * 0.15);

      let value = Math.round(baseValue * (1 + variance) * splitMultiplier);

      // Add day-of-week patterns
      if (groupBy === 'day_of_week') {
        const dayMultipliers = [1.2, 1.1, 1.0, 1.15, 0.9, 0.4, 0.3]; // Mon-Sun
        value = Math.round(value * dayMultipliers[index]);
      }

      // Add hour-of-day patterns
      if (groupBy === 'hour_of_day') {
        const hourMultiplier = index >= 8 && index <= 17 ? 1 + ((index - 8) * 0.05) : 0.2;
        value = Math.round(value * hourMultiplier);
      }

      dataPoint[key] = Math.max(0, value);
    });

    return dataPoint;
  });
};

const getBaseValue = (metric: MetricType, index: number, total: number): number => {
  // Create a slight upward trend
  const trendFactor = 1 + (index / total) * 0.2;

  switch (metric) {
    case 'tickets_created':
      return Math.round(25 * trendFactor);
    case 'tickets_resolved':
      return Math.round(22 * trendFactor);
    case 'avg_resolution_time':
      return Math.round(35 - (index / total) * 5); // Improving over time
    case 'sla_breach_count':
      return Math.round(3 - (index / total) * 1.5); // Improving over time
    case 'backlog_size':
      return Math.round(45 + Math.sin(index / 3) * 10);
    default:
      return 20;
  }
};

// ============================================================================
// SUMMARY CALCULATIONS
// ============================================================================

const calculateSummary = (data: DataPoint[], splitBy: SplitByType) => {
  const keys = Object.keys(data[0] || {}).filter((k) => k !== 'label');

  let total = 0;
  let values: number[] = [];

  data.forEach((point) => {
    keys.forEach((key) => {
      const val = Number(point[key]) || 0;
      total += val;
      values.push(val);
    });
  });

  const avg = values.length > 0 ? total / (splitBy === 'none' ? data.length : values.length) : 0;
  const max = Math.max(...values);
  const min = Math.min(...values.filter((v) => v > 0));
  const peakIndex = values.indexOf(max);
  const peakLabel = data[Math.floor(peakIndex / keys.length)]?.label || '-';

  // Calculate trend (compare first half to second half)
  const midpoint = Math.floor(values.length / 2);
  const firstHalf = values.slice(0, midpoint).reduce((a, b) => a + b, 0) / midpoint;
  const secondHalf = values.slice(midpoint).reduce((a, b) => a + b, 0) / (values.length - midpoint);
  const trendPercent = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

  return {
    total: splitBy === 'none' ? total : Math.round(total / keys.length),
    average: Math.round(avg * 10) / 10,
    peak: { value: max, label: peakLabel },
    min,
    trend: {
      value: Math.abs(Math.round(trendPercent)),
      direction: trendPercent > 2 ? 'up' : trendPercent < -2 ? 'down' : 'neutral',
    },
  };
};

// ============================================================================
// COMPONENT
// ============================================================================

export const AnalyticsExplorer: React.FC = () => {
  const ticketRepo = useTicketRepository();
  const techRepo = useTechRepository();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    metric: 'tickets_created',
    timeRange: '30d',
    groupBy: 'day',
    splitBy: 'none',
    chartType: 'line',
    priorities: [],
  });

  // Load data from repositories
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [loadedTickets, loadedTechs] = await Promise.all([
          ticketRepo.getAll(),
          techRepo.getAll(),
        ]);
        if (mounted) {
          setTickets(loadedTickets);
          setTechs(loadedTechs);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load analytics data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [ticketRepo, techRepo]);

  const updateFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Generate chart data
  const chartData = useMemo(
    () => generateTimeSeriesData(filters, tickets, techs),
    [filters, tickets, techs]
  );

  // Get data keys for chart (excluding 'label')
  const dataKeys = useMemo(() => {
    if (chartData.length === 0) return [];
    return Object.keys(chartData[0]).filter((k) => k !== 'label');
  }, [chartData]);

  // Calculate summary
  const summary = useMemo(
    () => calculateSummary(chartData, filters.splitBy),
    [chartData, filters.splitBy]
  );

  // Get color for a data key
  const getColor = (key: string, index: number): string => {
    if (filters.splitBy === 'none') return PRIMARY_COLOR;
    if (filters.splitBy === 'priority') {
      return SPLIT_COLORS.priority[key as keyof typeof SPLIT_COLORS.priority] || PRIMARY_COLOR;
    }
    const colors = SPLIT_COLORS[filters.splitBy] || SPLIT_COLORS.tech;
    return colors[index % colors.length];
  };

  // Get metric info
  const metricInfo = METRIC_OPTIONS.find((m) => m.value === filters.metric);

  // Render chart based on type
  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    const commonAxisProps = {
      axisLine: false,
      tickLine: false,
      tick: { fontSize: 12, fill: '#605e5c' },
    };

    const tooltipStyle = {
      contentStyle: {
        backgroundColor: '#fff',
        border: '1px solid #e1dfdd',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        fontSize: '13px',
        padding: '12px 16px',
      },
      labelStyle: {
        fontWeight: 600,
        marginBottom: '8px',
        color: '#323130',
      },
    };

    const legendProps = {
      wrapperStyle: { fontSize: '12px', paddingTop: '16px' },
      iconType: 'circle' as const,
      iconSize: 8,
    };

    if (filters.chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" {...commonAxisProps} />
            <YAxis {...commonAxisProps} />
            <Tooltip {...tooltipStyle} formatter={(value: number) => [`${value} ${metricInfo?.unit}`, '']} />
            {dataKeys.length > 1 && <Legend {...legendProps} />}
            {dataKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key === 'value' ? metricInfo?.label : key}
                fill={getColor(key, index)}
                radius={[4, 4, 0, 0]}
                maxBarSize={dataKeys.length > 1 ? 24 : 48}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (filters.chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart {...commonProps}>
            <defs>
              {dataKeys.map((key, index) => (
                <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={getColor(key, index)} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={getColor(key, index)} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" {...commonAxisProps} />
            <YAxis {...commonAxisProps} />
            <Tooltip {...tooltipStyle} formatter={(value: number) => [`${value} ${metricInfo?.unit}`, '']} />
            {dataKeys.length > 1 && <Legend {...legendProps} />}
            {dataKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={key === 'value' ? metricInfo?.label : key}
                stroke={getColor(key, index)}
                strokeWidth={2}
                fill={`url(#gradient-${key})`}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    // Default: Line chart
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="label" {...commonAxisProps} />
          <YAxis {...commonAxisProps} />
          <Tooltip {...tooltipStyle} formatter={(value: number) => [`${value} ${metricInfo?.unit}`, '']} />
          {dataKeys.length > 1 && <Legend {...legendProps} />}
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={key === 'value' ? metricInfo?.label : key}
              stroke={getColor(key, index)}
              strokeWidth={2.5}
              dot={{ fill: getColor(key, index), strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: getColor(key, index), stroke: '#fff', strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="analytics-explorer">
      {/* Controls Bar */}
      <div className="analytics-explorer__controls">
        <div className="analytics-explorer__control-group">
          <label className="analytics-explorer__control-label">Metric</label>
          <AppSelect
            value={filters.metric}
            onChange={(e) => updateFilter('metric', e.target.value as MetricType)}
            size="small"
            sx={{ minWidth: 180 }}
            options={METRIC_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          />
        </div>

        <div className="analytics-explorer__control-group">
          <label className="analytics-explorer__control-label">Time Range</label>
          <AppSelect
            value={filters.timeRange}
            onChange={(e) => updateFilter('timeRange', e.target.value as TimeRangeType)}
            size="small"
            sx={{ minWidth: 140 }}
            options={TIME_RANGE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          />
        </div>

        <div className="analytics-explorer__control-group">
          <label className="analytics-explorer__control-label">Group By</label>
          <AppSelect
            value={filters.groupBy}
            onChange={(e) => updateFilter('groupBy', e.target.value as GroupByType)}
            size="small"
            sx={{ minWidth: 150 }}
            options={GROUP_BY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          />
        </div>

        <div className="analytics-explorer__control-group">
          <label className="analytics-explorer__control-label">Split By</label>
          <AppSelect
            value={filters.splitBy}
            onChange={(e) => updateFilter('splitBy', e.target.value as SplitByType)}
            size="small"
            sx={{ minWidth: 150 }}
            options={SPLIT_BY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          />
        </div>

        <div className="analytics-explorer__control-divider" />

        <div className="analytics-explorer__chart-type-group">
          {CHART_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`analytics-explorer__chart-type-btn ${
                filters.chartType === opt.value ? 'analytics-explorer__chart-type-btn--active' : ''
              }`}
              onClick={() => updateFilter('chartType', opt.value)}
              title={opt.label}
            >
              {opt.icon}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Container */}
      <div className="analytics-explorer__chart-container">
        <div className="analytics-explorer__chart-header">
          <div>
            <h3 className="analytics-explorer__chart-title">{metricInfo?.label}</h3>
            <p className="analytics-explorer__chart-subtitle">
              {TIME_RANGE_OPTIONS.find((t) => t.value === filters.timeRange)?.label}
              {filters.splitBy !== 'none' && ` • ${SPLIT_BY_OPTIONS.find((s) => s.value === filters.splitBy)?.label}`}
            </p>
          </div>
        </div>
        <div className="analytics-explorer__chart">{renderChart()}</div>
      </div>

      {/* Summary Cards */}
      <div className="analytics-explorer__summary">
        <div className="analytics-explorer__summary-card">
          <span className="analytics-explorer__summary-label">Total</span>
          <span className="analytics-explorer__summary-value">{summary.total.toLocaleString()}</span>
          <span className="analytics-explorer__summary-unit">{metricInfo?.unit}</span>
        </div>
        <div className="analytics-explorer__summary-card">
          <span className="analytics-explorer__summary-label">Average</span>
          <span className="analytics-explorer__summary-value">{summary.average.toLocaleString()}</span>
          <span className="analytics-explorer__summary-unit">per period</span>
        </div>
        <div className="analytics-explorer__summary-card">
          <span className="analytics-explorer__summary-label">Peak</span>
          <span className="analytics-explorer__summary-value">{summary.peak.value.toLocaleString()}</span>
          <span className="analytics-explorer__summary-unit">{summary.peak.label}</span>
        </div>
        <div className="analytics-explorer__summary-card">
          <span className="analytics-explorer__summary-label">Trend</span>
          <span
            className={`analytics-explorer__summary-value analytics-explorer__summary-value--${summary.trend.direction}`}
          >
            {summary.trend.direction === 'up' && '+'}
            {summary.trend.direction === 'down' && '-'}
            {summary.trend.value}%
          </span>
          <span className="analytics-explorer__summary-unit">vs prior period</span>
        </div>
      </div>
    </div>
  );
};
